import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button, Heading, Notice, Text } from "@smooth/design-system-components"
import { DunningDialog } from "./DunningDialog"
import { BatchCaseResult } from "../evaluation/batch/BatchCaseResult"
import { isRegressionRun } from "../evaluation/batch/types"
import { useEvaluationBatches } from "../evaluation/batch/useEvaluationBatches"
import { useCaseActions } from "../evaluation/components/CaseActions"
import { CaseCreator } from "../evaluation/components/CaseCreator"
import { CaseList } from "../evaluation/components/CaseList"
import { usePromptChoice } from "../evaluation/components/PromptPicker"
import { CaseWorkspace } from "../evaluation/components/CaseWorkspace"
import { FlowList } from "../evaluation/components/FlowList"
import { FlowTimeline } from "../evaluation/components/FlowTimeline"
import { buildFlows, flowOf, groupByCustomer } from "../evaluation/flows"
import { StatusMessage } from "../evaluation/components/parts"
import { SETTINGS_TAB_LABELS } from "../evaluation/navigation"
import type { Locate, RegisterLeaveGuard, SettingsLocation } from "../evaluation/navigation"
import { PartialImportError, evaluationError, libraryErrorMessage } from "../evaluation/errors"
import { useEvaluationSandbox } from "../evaluation/useEvaluationSandbox"
import { CUT_AFTER_MS } from "../evaluation/components/ImportTimelinePicker"
import type { TimelinePoint } from "../evaluation/components/ImportTimelinePicker"
import {
  IMPORT_CASE_SET,
  continuationOf,
  copyOf,
  draftFromImport,
  emptyCase,
  isArchived,
  shiftIso,
} from "../evaluation/types"
import type {
  DraftSource,
  EvaluationCase,
  EvaluationRun,
  ImportCustomer,
  ImportSource,
} from "../evaluation/types"

/** 読み込んだケースは既定の箱に入れる（箱の指定は読み込み結果にそのまま返り、作成フォームの初期値になる） */
const withImportCaseSet = (source: ImportSource): ImportSource =>
  IMPORT_CASE_SET ? { ...source, caseSet: IMPORT_CASE_SET } : source

/**
 * 開く先。ケース、保存前の下書き（出どころ付き）、「ケースを作成」の作り方の選択、または一覧（ケースを閉じて一覧に戻る）
 */
type Choice = EvaluationCase | { draft: EvaluationCase; source: DraftSource } | "create" | "list"

/** 未保存の変更があるときに確認してから行う操作：別のケースを開く、または別のタブ（プロンプトの比較等）へ移る */
type Pending = { choice: Choice } | { leave: SettingsLocation }

/** まとめて生成のうち、まだ終わっていない項目の状態 */
const UNFINISHED_ITEM_STATUSES = new Set(["pending", "running", "waiting_retry"])

type Props = {
  /** URL で指定された、突き合わせるプロンプトの比較の実行と開くケース（プロンプトの比較から移ってきたとき等） */
  batchId?: number | null
  caseId?: number | null
  /** 開いたケース・突き合わせる実行を URL に反映し、プロンプトの比較へ戻る */
  onLocate?: Locate
  /**
   * cases：標準ケースを期待する対応ごとに詰める（1件ずつ試す）。
   * flows：お客様の流れ（実在のお客様の会話を時点ごとに追う実験ノート）。お客様ごとの流れと時点のタイムラインを出す
   */
  variant?: "cases" | "flows"
  /** お客様の流れで絞るお客様の督促ケース ID（督促ケース詳細の「検証で追う」から開いたとき） */
  source?: number | null
  /** ページ上部のタブの切り替えを、未保存の変更の確認に通すための登録先 */
  onRegisterLeaveGuard?: RegisterLeaveGuard
  /** URL で指定されたケースを開いた元。regression ならヘッダーの戻り先をプロンプトの比較の結果にする */
  from?: SettingsLocation["from"]
}

/**
 * 督促 AIドラフトを検証ケース（架空、または実際の督促ケースを伏せ字にして読み込んだもの）で検証する画面。
 * 左にケース一覧、右に選んだケースの読み取り表示／編集・生成・結果・履歴を置く。
 * プロンプトの比較の結果と突き合わせ、期待する対応に合わなかったケースから詰められるようにする
 */
export function DunningEvaluationSandbox({
  batchId: requestedBatchId,
  caseId: requestedCaseId,
  onLocate,
  variant = "cases",
  source = null,
  onRegisterLeaveGuard,
  from = null,
}: Props = {}) {
  const flowMode = variant === "flows"
  const tab = flowMode ? ("flows" as const) : ("sandbox" as const)
  const [selected, setSelected] = useState<EvaluationCase | null>(null)
  const [creating, setCreating] = useState(false)
  // 保存前の下書き（督促ケースからの読み込み・次の時点）の出どころ
  const [draftSource, setDraftSource] = useState<DraftSource | null>(null)
  // 未保存の変更があるときに別のケースを開こうとしたら、確認ダイアログを出して開く先を覚えておく
  const [pending, setPending] = useState<Pending | null>(null)
  const [dirty, setDirty] = useState(false)
  // プロンプトの比較の結果から開いたケースなら、その実行（ヘッダーの「← 比較の結果に戻る」の戻り先）
  const [returnBatch, setReturnBatch] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const api = useEvaluationSandbox(selected?.id)
  // しまったケースも含む。件数・複製の元・「すべて」の件数はしまったケースを除いて数える
  const cases = api.library.data?.data ?? []
  const activeCases = cases.filter((record) => !isArchived(record))
  const terminals = api.library.data?.terminals ?? {}
  const sourceUsers = api.library.data?.sourceUsers ?? {}
  // お客様の流れ：お客様ごとの流れ（督促ケース詳細から開いたときはそのお客様だけ）
  const flows = useMemo(() => buildFlows(cases), [cases])
  const customers = useMemo(
    () =>
      groupByCustomer(flows).filter(
        (customer) => !source || customer.sourceDunningCaseId === source
      ),
    [flows, source]
  )
  const selectedFlow = flowMode ? flowOf(flows, selected?.id || selected?.parentCaseId) : undefined

  // 突き合わせるプロンプトの比較の実行。URL で指定されていればその実行、なければ最新の実行（一覧を読んでから決める）
  const [linkedBatchId, setLinkedBatchId] = useState<number | null>(requestedBatchId ?? null)
  const batches = useEvaluationBatches(linkedBatchId)
  const batchList = batches.list.data?.data ?? []
  const batchDetail =
    batches.detail.data?.data.id === linkedBatchId ? batches.detail.data : undefined
  // 突き合わせるのはプロンプトの比較の実行（お客様の流れを丸ごと流した実行は除く）
  const latestRegressionRun = batchList.find(isRegressionRun)
  if (linkedBatchId == null && latestRegressionRun) setLinkedBatchId(latestRegressionRun.id)
  const [appliedBatch, setAppliedBatch] = useState(requestedBatchId ?? null)
  if (requestedBatchId && requestedBatchId !== appliedBatch) {
    setAppliedBatch(requestedBatchId)
    setLinkedBatchId(requestedBatchId)
  }
  // お客様の流れではプロンプトの比較と突き合わせない（プロンプトの比較とは目的が違う）
  const linked = useMemo(
    () =>
      !flowMode && batchDetail
        ? {
            batchId: batchDetail.data.id,
            items: new Map(batchDetail.items.map((item) => [item.caseId, item])),
          }
        : undefined,
    [batchDetail, flowMode]
  )
  const linkedItem = selected?.id ? linked?.items.get(selected.id) : undefined

  // 一覧で選んだケースのまとめて生成。プロンプトは有効な版（一覧では版を選ばない）。
  // 走っている実行（始めた実行、なければ実行中の実行）の終わっていない項目を、一覧で「生成中」にする
  const promptChoice = usePromptChoice()
  const [startedBatchId, setStartedBatchId] = useState<number | null>(null)
  const runningBatchId =
    startedBatchId ?? batchList.find((batch) => batch.status === "running")?.id ?? null
  const generation = useEvaluationBatches(runningBatchId)
  const generationDetail =
    generation.detail.data?.data.id === runningBatchId ? generation.detail.data : undefined
  const generationRunning = generationDetail?.data.status === "running"
  const generatingIds = useMemo(
    () =>
      new Set(
        generationDetail && generationRunning
          ? generationDetail.items
              .filter((item) => UNFINISHED_ITEM_STATUSES.has(item.status))
              .map((item) => item.caseId)
          : []
      ),
    [generationDetail, generationRunning]
  )
  // 走っているのを見た実行（または始めた実行）が終わったら、一覧の状態（生成済み・生成失敗）を読み直す
  const watchedBatchId = useRef<number | null>(null)
  const runningDetailId = generationRunning ? generationDetail.data.id : null
  useEffect(() => {
    if (runningDetailId != null) watchedBatchId.current = runningDetailId
  }, [runningDetailId])
  const finishedBatchId = generationDetail && !generationRunning ? generationDetail.data.id : null
  const refreshAfterGeneration = api.library.mutate
  const refreshBatchList = batches.list.mutate
  useEffect(() => {
    if (finishedBatchId == null) return
    if (finishedBatchId !== watchedBatchId.current && finishedBatchId !== startedBatchId) return
    watchedBatchId.current = null
    setStartedBatchId(null)
    void refreshAfterGeneration?.()
    void refreshBatchList?.()
  }, [finishedBatchId, startedBatchId, refreshAfterGeneration, refreshBatchList])
  const generateMany = async (ids: number[]) => {
    if (!promptChoice.prompt) throw new Error("プロンプトを読み込めていません。")
    const created = await generation.start({ ...promptChoice.prompt, caseIds: ids })
    setStartedBatchId(created.id)
  }
  // 一覧でまとめてしまう・戻す（一覧はケースを閉じているときだけ出るので、開いているケースは変わらない）
  const archiveMany = async (ids: number[], archived: boolean) => {
    await api.archiveMany(ids, archived)
  }
  const regressionMany = async (ids: number[], included: boolean) => {
    await api.setRegression(ids, included)
  }

  const refreshLibrary = useCallback(() => void api.library.mutate(), [api.library])
  const open = (choice: Choice, back: number | null = null) => {
    const draft = typeof choice !== "string" && "draft" in choice ? choice : null
    const record = typeof choice === "string" ? null : "draft" in choice ? choice.draft : choice
    setReturnBatch(back)
    setCreating(choice === "create")
    setSelected(record)
    setDraftSource(draft?.source ?? null)
    // 読み込み・続きの下書きは、保存するまで離れるときに確認する
    setDirty(!!draft)
    setError(null)
    setPending(null)
    // 開いた保存済みのケースを URL に残す（再読み込み・プロンプトの比較との行き来で戻れるように）
    onLocate?.(
      {
        tab,
        batch: flowMode ? null : linkedBatchId,
        case: record?.id || null,
        ...(flowMode ? { source } : {}),
        ...(back ? { from: "regression" as const } : {}),
      },
      { replace: true }
    )
  }
  const choose = (choice: Choice, back: number | null = null) => {
    if (busy) return
    if (dirty) {
      setPending({ choice })
      return
    }
    open(choice, back)
  }
  /** 別のタブ（プロンプトの比較・ページ上部のタブ）へ移る。未保存の変更があれば確認する */
  const leave = (to: SettingsLocation) => {
    if (dirty) {
      // 開いているタブをもう一度押しただけなら、編集はそのまま（離れないので確認も出さない）
      if (to.tab !== tab) setPending({ leave: to })
      return
    }
    onLocate?.(to)
  }
  // ページ上部のタブの切り替えも、この画面の確認（別のケースを開くときと同じダイアログ）を通す
  useEffect(() => {
    onRegisterLeaveGuard?.(leave)
    return () => onRegisterLeaveGuard?.(null)
  })
  const confirmPending = () => {
    if (!pending) return
    if ("choice" in pending) {
      open(pending.choice)
      return
    }
    setDirty(false)
    setPending(null)
    onLocate?.(pending.leave)
  }

  // URL でケースを指定されたら（プロンプトの比較から移ってきたとき等）、そのケースを開く
  const [appliedCase, setAppliedCase] = useState<number | null>(null)
  const requestedCase = requestedCaseId
    ? cases.find((record) => record.id === requestedCaseId)
    : undefined
  useEffect(() => {
    if (!requestedCase || requestedCase.id === appliedCase) return
    setAppliedCase(requestedCase.id)
    const back = !flowMode && from === "regression" ? (requestedBatchId ?? null) : null
    if (selected?.id !== requestedCase.id) choose(requestedCase, back)
  })
  const copy = async (record: EvaluationCase) => {
    setBusy(true)
    try {
      open(await api.save(copyOf(record)))
    } finally {
      setBusy(false)
    }
  }
  const load = async (source: ImportSource) => {
    const preview = await api.importPreview(withImportCaseSet(source))
    open({ draft: draftFromImport(preview), source: { kind: "import", preview } })
  }
  /**
   * 流れの無いお客様の知らせから、今の時点の会話を読み込む。「ケースを作成」の読み込みと違い受け皿のフォームが無いので、
   * 失敗（督促ケースが無い 404 など）は画面上部の知らせにサーバの理由を出す
   */
  const loadCurrent = async (dunningCaseId: number) => {
    setBusy(true)
    setError(null)
    try {
      await load({ dunningCaseId })
    } catch (e) {
      setError(evaluationError(e))
    } finally {
      setBusy(false)
    }
  }
  /**
   * お客様の会話から選んだ時点ごとに、その時点の状態で読み込み、期待する対応を付けて保存する。最後に作ったケースを開く。
   * 古い時点から順に保存し、2つ目以降は1つ前の時点を元のケース（parentCaseId）にして、お客様の流れで1本の流れとして並べる
   */
  const loadPoints = async (customer: ImportCustomer, points: TimelinePoint[]) => {
    setBusy(true)
    let last: EvaluationCase | null = null
    try {
      const ordered = [...points].sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
      let saved = 0
      for (const point of ordered) {
        try {
          const preview = await api.importPreview(
            withImportCaseSet({ ...customer, asOf: shiftIso(point.at, CUT_AFTER_MS) })
          )
          last = await api.save({
            ...draftFromImport(preview),
            expectedTerminal: point.expectedTerminal,
            ...(last ? { parentCaseId: last.id } : {}),
          })
          saved += 1
        } catch (e) {
          // 途中で失敗したときは、保存できた分（お客様の流れに残る）と失敗した時点を伝える
          if (saved === 0) throw e
          throw new PartialImportError(
            `${ordered.length}時点のうち${saved}時点までを保存しました（${saved + 1}時点目で失敗：${evaluationError(e)}）。保存した分はお客様の流れに残っています。`
          )
        }
      }
      if (last) open(last)
    } finally {
      setBusy(false)
    }
  }
  /**
   * 次の時点の下書きを開く。生成結果があれば生成時のケースに AIドラフトを、無ければ今のケースに当社の空の送信を足し、
   * 末尾にお客様の空の返事を置く
   */
  const continueFrom = async (run: EvaluationRun | null) => {
    if (!selected) return
    const base = run ? (await api.inspect(run.id)).caseSnapshot : selected
    const { draft, info } = continuationOf(
      {
        ...base,
        id: selected.id,
        sourceDunningCaseId: selected.sourceDunningCaseId,
        sourceUserId: selected.sourceUserId,
      },
      run
    )
    choose({ draft, source: { kind: "continuation", info } })
  }

  // しまう・戻す・削除（「…」メニューと、しまったケースの知らせ）。しまう・戻すは内容を変えないので編集中の入力はそのまま
  const savedCase = selected?.id ? selected : null
  const caseActions = useCaseActions({
    record: savedCase ?? emptyCase(),
    runCount: api.detail.data ? api.detail.data.runs.length : null,
    busy,
    onArchive: async (archived) => {
      if (!savedCase) return
      const updated = await api.archive(savedCase, archived)
      setSelected((current) =>
        current?.id === updated.id
          ? {
              ...current,
              archivedAt: updated.archivedAt,
              archivedBySmootherId: updated.archivedBySmootherId,
            }
          : current
      )
    },
    // 編集中は出さない（保存すると画面の箱で上書きされるため）
    onRegression: dirty
      ? undefined
      : async (included) => {
          if (!savedCase) return
          const updated = await api.setRegression([savedCase.id], included)
          // 箱だけの変更は版が変わらないので、開いているケースの箱も読み直した値で書き換える
          // （外したときの箱はサーバーが決める。読み直せなかったときは、入れたなら core、外したなら元のまま）
          const found = updated.find((record) => record.id === savedCase.id)
          setSelected((current) =>
            current?.id === savedCase.id
              ? {
                  ...current,
                  caseSet: found ? found.caseSet : included ? "core" : current.caseSet,
                }
              : current
          )
        },
    onRemove: async () => {
      if (!savedCase) return
      await api.remove(savedCase)
      setSelected(null)
      setDraftSource(null)
      setDirty(false)
      onLocate?.(
        {
          tab,
          batch: flowMode ? null : linkedBatchId,
          case: null,
          ...(flowMode ? { source } : {}),
        },
        { replace: true }
      )
    },
  })

  // 一覧のキャッシュは古いことがあるので、詳細 API の lockVersion が新しければそちらで開き直す
  // （古い lockVersion のまま保存・生成すると 409 になる）。編集中は入力を守るため上書きしない
  const fresh = api.detail.data?.data
  useEffect(() => {
    if (!fresh || !selected || dirty) return
    if (fresh.id === selected.id && fresh.lockVersion > selected.lockVersion) setSelected(fresh)
  }, [fresh, selected, dirty])

  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [dirty])

  // ケースを開いたら一覧を畳み、督促ケース詳細と同じ枠（ヘッダー・左右のペイン）を幅いっぱいに出す。
  // 一覧へは枠のヘッダーの「← 一覧に戻る」で戻る（督促ケース詳細と同じ）
  const detailOpen = !!selected && !creating
  const detailRef = useRef<HTMLElement>(null)
  useEffect(() => {
    // 縦一列の表示（xl 未満）では、枠の上端が共通ヘッダーの下に来るまで送る（xl ではページはスクロールしないので動かない）
    if (detailOpen) detailRef.current?.scrollIntoView?.({ block: "start" })
  }, [detailOpen, selected?.id])
  const workspace = selected && (
    <CaseWorkspace
      // 箱だけの保存は版が変わらないので、箱も含めて開き直す（編集を閉じる）
      key={`${selected.id}-${selected.lockVersion}-${selected.caseSet ?? ""}-${draftSource ? selected.title : ""}`}
      initial={selected}
      api={api}
      terminals={terminals}
      draftSource={draftSource}
      onContinue={continueFrom}
      actions={savedCase ? caseActions.menu : undefined}
      onBack={
        returnBatch
          ? () => leave({ tab: "regression", batch: returnBatch, case: selected.id || null })
          : () => choose("list")
      }
      backLabel={returnBatch ? "← 比較の結果に戻る" : "← 一覧"}
      notice={savedCase ? caseActions.notice : undefined}
      rightHead={
        selectedFlow && (
          <FlowTimeline
            key={selectedFlow.root.id}
            flow={selectedFlow}
            sourceUsers={sourceUsers}
            terminals={terminals}
            selectedId={selected.id || null}
            disabled={busy}
            onChoose={(point) => choose(point.record)}
            onFlowGenerated={refreshLibrary}
          />
        )
      }
      linkedResult={
        linked && linkedItem && <BatchCaseResult item={linkedItem} terminals={terminals} />
      }
      onDirty={setDirty}
      onBusy={setBusy}
      onSaved={(record) => {
        const created = !selected.id
        setSelected(record)
        setDraftSource(null)
        setDirty(false)
        // 作ったケースを URL に残す（再読み込みで同じ時点に戻れるように）
        if (created)
          onLocate?.(
            {
              tab,
              batch: flowMode ? null : linkedBatchId,
              case: record.id,
              ...(flowMode ? { source } : {}),
            },
            { replace: true }
          )
      }}
    />
  )

  return (
    <div
      data-smooth-ds
      className={`flex flex-col gap-2 bg-bg-default text-body text-text-default ${
        detailOpen ? "xl:min-h-0 xl:flex-1" : ""
      }`}
    >
      {!detailOpen && (
        <>
          {flowMode ? (
            <>
              <Heading level={2}>お客様の流れ</Heading>
              <Text size="sm">
                実在のお客様の会話（督促ケースから伏せ字にして読み込んだもの）を時点ごとに追い、各時点で期待する対応とAIの出力を見比べる実験ノートです。プロンプトやフローチャートの議論に使います。LINE送信や実契約の更新は行いません。
              </Text>
            </>
          ) : (
            <Heading level={2}>AIドラフト検証</Heading>
          )}
          <div className="my-2 flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={() => choose("create")} disabled={busy}>
              {flowMode ? "お客様の会話を読み込む" : "ケースを作成"}
            </Button>
          </div>
          {flowMode && source && api.library.data && customers.length === 0 && (
            <Notice tone="info" variant="card">
              <div className="flex flex-col gap-2">
                <Text>督促ケース #{source} のお客様の流れはまだありません。</Text>
                <div>
                  <Button onClick={() => void loadCurrent(source)} disabled={busy}>
                    今の時点の会話を読み込む
                  </Button>
                </div>
              </div>
            </Notice>
          )}
        </>
      )}
      {(error || api.library.error) && (
        <div role="alert">
          <Notice tone="error" variant="card">
            {error ?? libraryErrorMessage(api.library.error)}
          </Notice>
        </div>
      )}
      {!api.library.data && !api.library.error && (
        <StatusMessage>ケースを読み込んでいます…</StatusMessage>
      )}
      {detailOpen ? (
        <section
          ref={detailRef}
          aria-label="選択中のケース"
          className="min-w-0 scroll-mt-[var(--header-stack-height,60px)] xl:flex xl:min-h-0 xl:flex-1 xl:flex-col"
        >
          {workspace}
        </section>
      ) : (
        <div className="grid border-t border-border-default min-[900px]:grid-cols-[320px_minmax(0,1fr)]">
          {flowMode ? (
            <FlowList
              loaded={!!api.library.data}
              customers={customers}
              sourceUsers={sourceUsers}
              selectedRootId={selectedFlow?.root.id ?? null}
              selectedCaseId={selected?.id ?? null}
              disabled={busy}
              onChoose={choose}
            />
          ) : (
            <CaseList
              loaded={!!api.library.data}
              cases={cases}
              terminals={terminals}
              selectedId={selected?.id ?? null}
              disabled={busy}
              onChoose={choose}
              generatingIds={generatingIds}
              onGenerateMany={promptChoice.prompt ? generateMany : undefined}
              onArchiveMany={archiveMany}
              onRegressionMany={regressionMany}
            />
          )}
          <section aria-label="選択中のケース" className="min-w-0 py-6 min-[900px]:px-8">
            {creating ? (
              <CaseCreator
                cases={activeCases}
                terminals={terminals}
                busy={busy}
                defaultMethod={flowMode ? "import" : "blank"}
                defaultDunningCaseId={flowMode ? source : null}
                onBlank={() => open(emptyCase())}
                onCopy={copy}
                onImport={load}
                onLoadTimeline={api.importTimeline}
                onImportPoints={loadPoints}
              />
            ) : flowMode ? (
              <div className="flex flex-col gap-2">
                <Heading level={3}>お客様の流れを選んでください</Heading>
                <Text>
                  時点ごとに、その時点までのお客様の発言・期待する対応・AIが選んだ対応と文面・メモを並べます。時点を選ぶと、入力の確認・編集・生成・続きの時点づくりができます。
                </Text>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Heading level={3}>検証したいケースを選んでください</Heading>
                <Text>
                  ケースを選ぶと、督促ケース詳細と同じ並びで会話と契約状況を表示します。プロンプトを選んでAIドラフトを生成できます。
                </Text>
              </div>
            )}
          </section>
        </div>
      )}
      {savedCase && caseActions.dialog}
      {pending && (
        <DunningDialog
          title="未保存の変更があります"
          description={
            "choice" in pending
              ? pending.choice === "list"
                ? "変更を破棄して一覧に戻りますか？"
                : "変更を破棄して別のケースを開きますか？"
              : `変更を破棄して${SETTINGS_TAB_LABELS[pending.leave.tab]}を開きますか？`
          }
          onClose={() => setPending(null)}
          footer={
            <>
              <Button variant="outline" onClick={() => setPending(null)}>
                編集を続ける
              </Button>
              <Button variant="danger" onClick={confirmPending}>
                {"choice" in pending && pending.choice === "list"
                  ? "変更を破棄して戻る"
                  : "変更を破棄して開く"}
              </Button>
            </>
          }
        >
          <Text>保存していない編集内容は失われます。</Text>
        </DunningDialog>
      )}
    </div>
  )
}
